import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { loadUiSetting, saveUiSetting } from '@/lib/ui-settings'

/** Zwischen welchen Anteilen (in Prozent) sich eine Teilung verschieben lässt und wo sie ohne Wahl steht. */
export interface TeilungGrenzen {
  vorgabe: number
  min: number
  max: number
}

/** Original und Erfassung der Eingangsrechnung: Anteil des Originals, Vorgabe wie bisher fünf Zwölftel. */
export const TEILUNG_VORGABE = 42
export const TEILUNG_MIN = 20
export const TEILUNG_MAX = 70
const STANDARD: TeilungGrenzen = { vorgabe: TEILUNG_VORGABE, min: TEILUNG_MIN, max: TEILUNG_MAX }

export const begrenzeTeilung = (anteil: number, g: TeilungGrenzen = STANDARD): number => (Number.isFinite(anteil) ? Math.min(g.max, Math.max(g.min, Math.round(anteil))) : g.vorgabe)

/**
 * Der Anteil aus der Stelle des Zeigers im Behälter — von links gezählt, mit `vonRechts` vom rechten Rand
 * (ein Seitenfeld am rechten Fensterrand wird breiter, wenn sein linker Rand nach links wandert).
 */
export const teilungAusZeiger = (zeigerX: number, behaelter: { left: number; width: number }, g: TeilungGrenzen = STANDARD, vonRechts = false): number => {
  if (behaelter.width <= 0) return g.vorgabe
  const links = ((zeigerX - behaelter.left) / behaelter.width) * 100
  return begrenzeTeilung(vonRechts ? 100 - links : links, g)
}

export interface TeilungOptionen extends Partial<TeilungGrenzen> {
  /** Folgt dem Benutzer, nicht dem Gerät — ein Anteil passt auf jeden Schirm (`ui-settings.keys.ts`: `split_…`). */
  schluessel: string
  /** Name des Griffs für Vorlesehilfen und als Hinweis beim Zeigen */
  label: string
  /** Der Anteil zählt vom rechten Rand des Behälters */
  vonRechts?: boolean
  /** Gemessen wird am Fenster statt an einem Behälter (Seitenfeld über der Seite) */
  amFenster?: boolean
}

/**
 * **Eine Breite verschieben und merken** (Auftraggeber 5.10.2026 für die Eingangsrechnung, 13.10.2026 für den
 * Posteingang): Der Rand zwischen zwei Flächen lässt sich greifen und ziehen. Gemerkt wird der Anteil je
 * Benutzer; Doppelklick stellt die Vorgabe wieder her, die Pfeiltasten verschieben in kleinen Schritten.
 *
 * Gezogen wird mit eingefangenem Zeiger — so läuft die Bewegung weiter, auch wenn der Zeiger über die
 * gezeichneten Seiten des Originals fährt.
 */
export function useTeilung(optionen: TeilungOptionen) {
  const { schluessel, label, vonRechts = false, amFenster = false } = optionen
  const grenzen = useRef<TeilungGrenzen>({ vorgabe: optionen.vorgabe ?? TEILUNG_VORGABE, min: optionen.min ?? TEILUNG_MIN, max: optionen.max ?? TEILUNG_MAX }).current
  const behaelter = useRef<HTMLDivElement>(null)
  const [anteil, setAnteil] = useState(() => begrenzeTeilung(loadUiSetting<{ left?: number } | null>(schluessel, null)?.left ?? grenzen.vorgabe, grenzen))
  const [zieht, setZieht] = useState(false)
  const aktuell = useRef(anteil)
  aktuell.current = anteil

  const setze = useCallback(
    (neu: number, merken: boolean) => {
      const wert = begrenzeTeilung(neu, grenzen)
      setAnteil(wert)
      if (merken) saveUiSetting(schluessel, { left: wert })
    },
    [grenzen, schluessel],
  )

  const flaeche = (): { left: number; width: number } | null => (amFenster ? { left: 0, width: window.innerWidth } : (behaelter.current?.getBoundingClientRect() ?? null))

  const griff = {
    role: 'separator' as const,
    'aria-orientation': 'vertical' as const,
    'aria-label': label,
    'aria-valuemin': grenzen.min,
    'aria-valuemax': grenzen.max,
    'aria-valuenow': anteil,
    tabIndex: 0,
    title: `Ziehen ändert die ${label} — Doppelklick stellt sie zurück`,
    onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      setZieht(true)
    },
    onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
      const f = flaeche()
      if (f) setze(teilungAusZeiger(e.clientX, f, grenzen, vonRechts), false)
    },
    onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
      e.currentTarget.releasePointerCapture(e.pointerId)
      setZieht(false)
      setze(aktuell.current, true)
    },
    onPointerCancel: () => setZieht(false),
    onDoubleClick: () => setze(grenzen.vorgabe, true),
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      e.preventDefault()
      // Der Griff folgt der Pfeiltaste: Zählt der Anteil von rechts, macht „links" ihn größer.
      const schritt = (e.key === 'ArrowLeft' ? -2 : 2) * (vonRechts ? -1 : 1)
      setze(aktuell.current + schritt, true)
    },
  }
  return { behaelter, anteil, zieht, griff }
}
